import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Check, Trash2, MessageSquare, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPrayers() {
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchPrayers();
  }, []);

  const fetchPrayers = async () => {
    try {
      const response = await axios.get(`${API}/prayers?public_only=false`, { withCredentials: true });
      setPrayers(response.data);
    } catch (error) {
      toast.error('Failed to load prayers');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAnswered = async (id, isAnswered) => {
    try {
      await axios.put(`${API}/prayers/${id}`, { is_answered: isAnswered }, { withCredentials: true });
      toast.success(isAnswered ? 'Marked as answered' : 'Marked as unanswered');
      fetchPrayers();
    } catch (error) {
      toast.error('Failed to update prayer');
    }
  };

  const handleTogglePublic = async (id, isPublic) => {
    try {
      await axios.put(`${API}/prayers/${id}`, { is_public: isPublic }, { withCredentials: true });
      toast.success(isPublic ? 'Made public' : 'Made private');
      fetchPrayers();
    } catch (error) {
      toast.error('Failed to update prayer');
    }
  };

  const handleAddNote = async (id) => {
    if (!note.trim()) return;
    try {
      await axios.put(`${API}/prayers/${id}`, { admin_note: note }, { withCredentials: true });
      toast.success('Note added');
      setNote('');
      setExpandedPrayer(null);
      fetchPrayers();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prayer request?')) return;
    try {
      await axios.delete(`${API}/prayers/${id}`, { withCredentials: true });
      toast.success('Prayer deleted');
      fetchPrayers();
    } catch (error) {
      toast.error('Failed to delete prayer');
    }
  };

  const filteredPrayers = prayers.filter(p => {
    if (filter === 'answered') return p.is_answered;
    if (filter === 'pending') return !p.is_answered;
    if (filter === 'public') return p.is_public;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-prayers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Prayer Requests</h1>
          <p className="text-charcoal/60">{prayers.length} total requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-charcoal/50" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="answered">Answered</SelectItem>
              <SelectItem value="public">Public Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-charcoal">{prayers.filter(p => !p.is_answered).length}</p>
          <p className="text-sm text-charcoal/60">Pending</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-olive">{prayers.filter(p => p.is_answered).length}</p>
          <p className="text-sm text-charcoal/60">Answered</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-gold">{prayers.filter(p => p.is_public).length}</p>
          <p className="text-sm text-charcoal/60">Public</p>
        </div>
      </div>

      <div className="space-y-4">
        {filteredPrayers.map((prayer) => (
          <motion.div
            key={prayer.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-charcoal">{prayer.name || 'Anonymous'}</h3>
                  {prayer.is_answered && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-olive/10 text-olive">Answered</span>
                  )}
                  {prayer.is_public && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gold/10 text-gold">Public</span>
                  )}
                </div>
                {prayer.email && (
                  <p className="text-sm text-charcoal/50">{prayer.email}</p>
                )}
                <p className="text-xs text-charcoal/40 mt-1">
                  {new Date(prayer.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpandedPrayer(expandedPrayer === prayer.id ? null : prayer.id)}
                  className="p-2 text-charcoal/50 hover:text-gold"
                  title="Add note"
                >
                  <MessageSquare size={18} />
                </button>
                <button
                  onClick={() => handleMarkAnswered(prayer.id, !prayer.is_answered)}
                  className={`p-2 ${prayer.is_answered ? 'text-olive' : 'text-charcoal/50 hover:text-olive'}`}
                  title={prayer.is_answered ? 'Mark unanswered' : 'Mark as answered'}
                >
                  <Check size={18} />
                </button>
                <button
                  onClick={() => handleDelete(prayer.id)}
                  className="p-2 text-charcoal/50 hover:text-red-500"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="bg-cream/50 rounded-lg p-4 mb-4">
              <p className="text-charcoal/80">{prayer.request}</p>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleTogglePublic(prayer.id, !prayer.is_public)}
                className={`text-sm ${prayer.is_public ? 'text-gold' : 'text-charcoal/50 hover:text-gold'}`}
              >
                {prayer.is_public ? 'Make Private' : 'Make Public'}
              </button>
            </div>
            
            {expandedPrayer === prayer.id && (
              <div className="mt-4 pt-4 border-t border-gold/10">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a private note..."
                  rows={2}
                  className="mb-2"
                />
                <Button
                  onClick={() => handleAddNote(prayer.id)}
                  size="sm"
                  className="bg-burgundy hover:bg-purple"
                >
                  Save Note
                </Button>
              </div>
            )}
            
            {prayer.admin_note && (
              <div className="mt-4 pt-4 border-t border-gold/10">
                <p className="text-sm text-charcoal/60">
                  <strong>Note:</strong> {prayer.admin_note}
                </p>
              </div>
            )}
          </motion.div>
        ))}
        
        {filteredPrayers.length === 0 && (
          <div className="text-center py-12 text-charcoal/50">
            <Heart size={48} className="mx-auto mb-4 opacity-20" />
            <p>No prayer requests found</p>
          </div>
        )}
      </div>
    </div>
  );
}
